import { AfterViewInit, Directive, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { fromEvent, Subject, race } from 'rxjs';

@Directive({
  selector: '[msInfiniteScroll]',
  standalone: false,
})
export class MatSelectInfiniteScrollDirective implements OnInit, OnDestroy, AfterViewInit {
  @Input() threshold = '15%';
  @Input() debounceTime = 150;
  @Input() complete!: boolean;
  @Output() infiniteScroll = new EventEmitter<void>();

  private panel!: Element;
  private thrPx = 0;
  private thrPc = 0;
  private destroyed = false;
  private destroyed$ = new Subject<boolean>();
  private panelClosed$ = new Subject<void>();

  constructor(
    private matSelect: MatSelect,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.evaluateThreshold();
  }

  ngAfterViewInit() {
    this.matSelect.openedChange.pipe(takeUntil(this.destroyed$)).subscribe((opened) => {
      if (opened) {
        // Material 21 MDC moved the panel inside a <ng-template cdk-connected-overlay>,
        // so the @ViewChild('panel') reference isn't populated synchronously with
        // openedChange — it's set only after CDK attaches the overlay (one macrotask
        // away). Poll a few animation frames for the panel element rather than racing
        // Angular's change detection.
        this.findPanelElement((panelEl) => {
          this.panel = panelEl;
          this.registerScrollListener();
        });
      } else {
        this.panelClosed$.next();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private findPanelElement(callback: (panel: HTMLElement) => void, attempt = 0): void {
    // If the directive was destroyed while a frame was queued, abort — otherwise
    // the late callback would call registerScrollListener after destroyed$ has
    // already completed, and takeUntil(race(...)) would never fire (a Subject
    // that completes without emitting doesn't trip takeUntil), leaking the
    // scroll subscription for the lifetime of the page.
    if (this.destroyed) return;
    const fromViewChild = this.matSelect.panel?.nativeElement as HTMLElement | undefined;
    if (fromViewChild) {
      callback(fromViewChild);
      return;
    }
    // Fall back to a direct DOM lookup — id is deterministic per mat-select instance.
    const byId = document.getElementById(`${this.matSelect.id}-panel`) as HTMLElement | null;
    if (byId) {
      callback(byId);
      return;
    }
    if (attempt < 10) {
      requestAnimationFrame(() => this.findPanelElement(callback, attempt + 1));
    } else {
      console.warn('[msInfiniteScroll] gave up looking for mat-select panel after 10 frames');
    }
  }

  private evaluateThreshold() {
    if (this.threshold.lastIndexOf('%') > -1) {
      this.thrPx = 0;
      this.thrPc = parseFloat(this.threshold) / 100;
    } else {
      this.thrPx = parseFloat(this.threshold);
      this.thrPc = 0;
    }
  }

  private registerScrollListener() {
    fromEvent(this.panel, 'scroll')
      .pipe(
        takeUntil(race(this.destroyed$, this.panelClosed$)),
        debounceTime(this.debounceTime),
        tap((event) => {
          this.handleScrollEvent(event);
        })
      )
      .subscribe();
  }

  private handleScrollEvent(event: any) {
    this.ngZone.runOutsideAngular(() => {
      if (this.complete) {
        return;
      }
      // Use the panel's actual scroll metrics. Under Material 21 (MDC) the
      // previous estimate (singleOptionHeight × matSelect.options.length) was
      // off — option padding, optgroup labels and the embedded search input
      // all add height the multiplication missed, so the user could scroll to
      // the actual bottom but the check still thought more scroll was ahead.
      const panel = event.target as HTMLElement;
      const remaining = panel.scrollHeight - (panel.clientHeight + panel.scrollTop);
      const threshold = this.thrPc !== 0 ? panel.scrollHeight * this.thrPc : this.thrPx;

      if (remaining <= threshold) {
        this.ngZone.run(() => this.infiniteScroll.emit());
      }
    });
  }
}
